/**
 * FMTopBar — Modern command-bar style topbar for the Fields Manager.
 *
 * Layout (left → right):
 *   [back]  Fields Manager  ›  [editable schema title]  · [draft pill]
 *                                                              [preview] [save ⌘S]
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Eye, Save, Loader2, Pencil,
} from 'lucide-react';

export default function FMTopBar({
  schemaName,
  onEditName,
  unsaved,
  saving,
  sectionCount,
  fieldCount,
  onBack,
  onPreview,
  onSave,
}) {
  const { t } = useTranslation();

  return (
    <header className="fmx-topbar">
      <div className="fmx-topbar__left">
        <button className="fmx-iconbtn" onClick={onBack} title={t('fieldsManager.topBar.goBack')}>
          <ArrowLeft />
        </button>
        <div className="fmx-divider" aria-hidden="true" />
        <span className="fmx-topbar__app">{t('fieldsManager.title')}</span>
        <span className="fmx-topbar__sep">/</span>
        <button
          type="button"
          className={`fmx-topbar__title ${schemaName ? '' : 'is-untitled'}`}
          onClick={onEditName}
          title={t('fieldsManager.topBar.clickToRename')}
        >
          <span className="fmx-topbar__title-text">
            {schemaName || t('fieldsManager.untitledSchema')}
          </span>
          <Pencil className="fmx-topbar__title-edit" />
        </button>

        {(sectionCount > 0 || fieldCount > 0) && (
          <span className="fmx-topbar__count" aria-label={t('fieldsManager.topBar.stats')}>
            {t('fieldsManager.topBar.sectionsCount', { count: sectionCount })}
            <span className="fmx-topbar__count-dot" />
            {t('fieldsManager.topBar.fieldsCount', { count: fieldCount })}
          </span>
        )}
      </div>

      <div className="fmx-topbar__right">
        <span className={`fmx-status ${unsaved ? 'fmx-status--unsaved' : 'fmx-status--saved'}`}>
          <span className="fmx-status__dot" aria-hidden="true" />
          {unsaved ? t('fieldsManager.unsavedChanges') : t('fieldsManager.topBar.allSaved')}
        </span>

        <button
          type="button"
          className="fmx-btn fmx-btn--ghost"
          onClick={onPreview}
          title={t('fieldsManager.sidebar.previewForm')}
        >
          <Eye />
          <span>{t('fieldsManager.preview')}</span>
        </button>

        <button
          type="button"
          className="fmx-btn fmx-btn--primary"
          onClick={onSave}
          disabled={saving}
          title={t('fieldsManager.topBar.saveShortcut')}
        >
          {saving ? (
            <>
              <Loader2 className="fmx-spin" />
              <span>{t('fieldsManager.saving')}</span>
            </>
          ) : (
            <>
              <Save />
              <span>{t('fieldsManager.topBar.save')}</span>
              <kbd className="fmx-kbd">⌘S</kbd>
            </>
          )}
        </button>
      </div>
    </header>
  );
}
